import { usePostShapefileGeojsonConversioni } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { DraggerInput, FormItem } from '@framework/form';
import { message } from 'antd';
import { RcFile } from 'antd/es/upload';
import { Feature, Polygon } from 'geojson';
import { GeoJSON } from 'ol/format';
import * as React from 'react';
import { useWatch } from 'react-hook-form';
import { Subject } from 'rxjs';
import { MapSubjectEvent } from '../../MapWidgetComponent';
import { MapUploadGeometriesType } from './MapUploadGeometriesSchema';

export interface IMapUploadGeometriesShapefileInputProps {
  mapSubject: React.RefObject<Subject<MapSubjectEvent>>;
  onClose: () => void;
}

const geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

function isGeoJSONFeatureCollection(
  data: any,
): data is GeoJSON.FeatureCollection {
  return (
    data != null &&
    typeof data === 'object' &&
    data.type === 'FeatureCollection' &&
    Array.isArray(data.features)
  );
}

export function MapUploadGeometriesShapefileInput(
  props: IMapUploadGeometriesShapefileInputProps,
) {
  const { mapSubject } = props;

  const selectedType: MapUploadGeometriesType | null = useWatch({
    name: 'type',
  });

  const convertShapefileToGeoJSON = usePostShapefileGeojsonConversioni();
  const isUploadingAt = React.useRef(Date.now());

  const handleUpload = async (allFiles: RcFile[]) => {
    if (Date.now() - isUploadingAt.current < 100) return;
    isUploadingAt.current = Date.now();

    const files = allFiles.filter(isShapefileExtension);
    const missingExtensions = missingShapefileExtensions(files);
    if (missingExtensions) {
      message.warning(`Non sono stati inclusi tutti i file necessari per lo Shapefile, mancano i seguenti: ${missingExtensions.join(', ')}`); // prettier-ignore
      return;
    }

    try {
      const data = await convertShapefileToGeoJSON.mutateAsync({
        data: {
          file: files.map(file => file as Blob),
        },
      });

      if (data.avviso?.messaggio != null) {
        message.error(`Non è stato possibile importare il file (MWSE002): ${data.avviso.messaggio}`); // prettier-ignore
        console.error("Errore durante l'importazione dello shapefile", data);
        return;
      }

      if (!isGeoJSONFeatureCollection(data?.geojson)) {
        message.error('Errore durante la conversione dello shapefile in GeoJSON: non è stato possibile ottenere le geometrie. (MWSE001)'); // prettier-ignore
        console.error('Errore durante la conversione dello shapefile in GeoJSON', data); // prettier-ignore
        return;
      }

      let conversionWarnings = 0;
      const convertedFeatures = data.geojson.features
        .map(feature => {
          if (feature.geometry.type === 'Polygon') {
            return feature;
          }

          // Le API restituiscono MultiPolygon come Polygon, ma con un solo poligono
          // (Coordinate[0]). Convertiamo quindi MultiPolygon in Polygon lato client.
          if (feature.geometry.type === 'MultiPolygon') {
            if (feature.geometry.coordinates.length !== 1) {
              console.warn('MultiPolygon con più di un poligono', feature);
              conversionWarnings++;
              return null;
            }

            return {
              ...feature,
              geometry: {
                ...feature.geometry,
                type: 'Polygon',
                coordinates: feature.geometry.coordinates[0],
              },
            };
          }
        })
        .filter((f): f is Feature<Polygon> => f != null)
        .map(feature => geoJSON.readFeature(feature));

      if (conversionWarnings > 0) {
        message.warning(`${conversionWarnings} geometrie del file non sono state riconosciute. Assicurarsi che siano tutti poligoni.`); // prettier-ignore
      }

      mapSubject.current?.next({
        type: 'upload',
        features: convertedFeatures,
      });
      props.onClose();
    } catch (e) {
      message.error(getNetworkErrorMessage(e, 'Errore durante la conversione dello shapefile. (MWSE005)')); // prettier-ignore
      console.error('Errore durante la conversione dello shapefile in GeoJSON', e); // prettier-ignore
    }
  };

  if (selectedType !== MapUploadGeometriesType.Shapefile) return null;

  return (
    <FormItem name="files" label="File">
      <DraggerInput
        name="files"
        accept={'.shp,.shx,.dbf'}
        multiple
        info="Selezionare i file che compongono lo Shapefile (.shp, .shx, .dbf)"
        showUploadList={false}
        beforeUpload={async (file, fileList) => {
          await handleUpload(fileList);
          return false;
        }}
      />
    </FormItem>
  );
}

function isShapefileExtension(file: RcFile) {
  return ['.shp', '.shx', '.dbf'].some(ext => file.name.endsWith(ext));
}

function missingShapefileExtensions(files: RcFile[]) {
  const missingExtensions = ['.shp', '.shx', '.dbf'].filter(
    ext => !files.some(f => f.name.endsWith(ext)),
  );

  if (missingExtensions.length === 0) return null;
  return missingExtensions;
}
